import { enhanceMarkerWithReservationInfo } from './markers';
import { Desk, Floor, Reservation } from '@ciao/console/src/swagger/models';
import { AxiosInstance } from 'axios';
import L, { LatLng } from 'leaflet';
import { DateTime, Duration, Interval } from 'luxon';
import {
	FloorData,
	ImageDimensions,
	Legend,
	MarkerClickCallback,
	MarkerDragCallback,
	MarkerStatus,
	WBMarker,
} from './types';

export const getImageDimensions = async (url: string): Promise<ImageDimensions> => {
	if (!url) throw new Error(`Can't resolve dimensions of url ${url}`);

	const im = new Image();
	im.src = url;

	await new Promise((resolve, reject) => {
		im.onload = resolve;
		im.onerror = reject;
	});

	return {
		width: im.width,
		height: im.height,
	};
};

export const getLegend = (): Legend => {
	return {
		[MarkerStatus.available]: { color: '#26CFA7', label: 'Available' },
		[MarkerStatus.partiallyAvailable]: { color: '#F7D734', label: 'Partially available' },
		[MarkerStatus.unavailable]: { color: '#DFE1E6', label: 'Unavailable' },
		[MarkerStatus.reserved]: { color: '#F03A47', label: 'Reserved' },
		[MarkerStatus.checkedIn]: { color: '#4D96F2', label: 'Checked in' },
	};
};

export const extractDeskNumber = (name: string) => {
	return name.replace(/[^\d]/g, '').trim().replace(/^0*/, '') || '?';
};

export const createIcon = (initials: string, status: MarkerStatus) =>
	L.divIcon({
		// iconUrl: 'my-icon.png',
		className: 'wbMarker', // Todo: add css and don't forget :focus
		iconSize: [30, 30],
		iconAnchor: [15, 15],
		popupAnchor: [0, 0],
		// shadowUrl: 'my-icon-shadow.png',
		// shadowSize: [68, 95],
		// shadowAnchor: [22, 94]
		html: `<div style="width:30px;height:30px;border-radius:50%;background:${
			getLegend()[status].color
		};text-align:center;color:white;line-height:30px;font-weight:bold">${initials}</div>`,
	});

// Todo: move reservation stuff to the generator function
export const generateMarkers = ({
	desks,
	onMarkerClick,
	onMarkerDrag,
	interval,
}: {
	desks: Desk[];
	interval?: Interval;
	onMarkerClick: MarkerClickCallback;
	onMarkerDrag?: MarkerDragCallback;
}): WBMarker[] => {
	const markers: WBMarker[] = [];

	for (const desk of desks) {
		// Don't render desks that have not been placed
		if (desk.x === null || desk.y === null) continue;

		const marker: WBMarker = {
			desk,
			status: desk.deletedAt ? MarkerStatus.unavailable : MarkerStatus.available,
			initials: desk.name ? extractDeskNumber(desk.name) : '?',
		};

		// Consider reservations for this marker and update status/initials accordingly
		if (desk.reservations && interval)
			enhanceMarkerWithReservationInfo(marker, <Reservation[]>desk.reservations, interval);

		// Create the actual (map) marker
		marker.marker = L.marker(L.latLng(0, 0), {
			icon: createIcon(marker.initials, marker.status),
			draggable: !!onMarkerDrag,
		})
			.on('click', () => {
				onMarkerClick(marker);
			})
			.on('dragend', async (e) => {
				if (!onMarkerDrag) return;

				// Couldn't find a better way to get the map this marker belongs to
				// Calculate the relative position of the marker (0 to 1 for both axes)
				const mapBounds = e.target._map.options.maxBounds;
				const markerLatLng: LatLng = e.target.getLatLng();

				const worldWidth = mapBounds.getEast() - mapBounds.getWest();
				const worldHeight = mapBounds.getNorth() - mapBounds.getSouth();

				const x = (markerLatLng.lng - mapBounds.getWest()) / worldWidth;
				const y = 1 + (markerLatLng.lat - mapBounds.getNorth()) / worldHeight;

				await onMarkerDrag(marker, Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y)));
			});

		markers.push(marker);
	}

	return markers;
};

export const getFloorData = async (floorId: number, axios: AxiosInstance): Promise<FloorData> => {
	const floorRes = await axios.get<Floor>(`/floors/${floorId}`);
	const floor = floorRes.data;

	if (!floor.floorplanUrl) throw new Error('Floor has no floorplan');

	const zonesRes = await axios.get(`/zones?floor=${floorId}`);

	const desks = [];

	for (const zone of zonesRes.data) {
		const desksRes = await axios.get(`/desks?zone=${zone.id}&withDeleted=true`);
		desks.push(...desksRes.data);
	}

	return {
		floor: <FloorData['floor']>floor, // TS doesn't pick up the floorplanUrl check up above
		zones: zonesRes.data,
		desks,
	};
};

export const getReservationIntervals = (reservation: Reservation) => {
	let reservationInterval: Interval;
	let checkinInterval: Interval | null = null;

	if (reservation.startAt && reservation.endAt) {
		reservationInterval = Interval.fromDateTimes(
			DateTime.fromSQL(reservation.startAt, { zone: 'utc' }),
			DateTime.fromSQL(reservation.endAt, { zone: 'utc' }),
		);
	} else {
		reservationInterval = Interval.fromDateTimes(
			DateTime.utc().startOf('day').plus({ hours: 8 }),
			DateTime.utc().startOf('day').plus({ hours: 17 }),
		);
	}

	if (reservation.checkinAt)
		checkinInterval = Interval.fromDateTimes(
			DateTime.fromSQL(reservation.checkinAt, { zone: 'utc' }),
			DateTime.fromSQL(reservation.checkoutAt || reservation.endAt || '17:00:00', { zone: 'utc' }),
		);

	return {
		reservationInterval,
		checkinInterval,
	};
};

export const getFreeTimeInInterval = (interval: Interval, intervals: Interval[]): Duration => {
	let duration = interval.toDuration();
	for (let interval of intervals) {
		duration = duration.minus(interval.toDuration());
	}

	return duration;
};
